import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import { dataSearchState } from "../state";
import {
  DEPARTMENT_FUNCTION_OPTIONS,
  DEPARTMENT_ID_TO_LABEL
} from "./employeesByDepartmentData";

const DepartmentSelect = ({
  value = null,
  onChange,
  excludedIds = [],
  placeholder = "Department",
  searchPlaceholder = "Search",
  defaultOpen = false
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const excludedSet = new Set(excludedIds);
  const baseOptions = DEPARTMENT_FUNCTION_OPTIONS;
  const filtered = search.trim()
    ? baseOptions.filter(
        o =>
          (o.label || "").toLowerCase().includes(search.toLowerCase()) ||
          (o.id || "").toLowerCase().includes(search.toLowerCase())
      )
    : baseOptions;

  const selectOne = id => {
    if (excludedSet.has(id) && id !== value) return;
    onChange(id);
    setOpen(false);
  };

  const label = value ? (DEPARTMENT_ID_TO_LABEL[value] || value) : null;

  return (
    <div className="employeesByDeptSelectWrap" ref={containerRef}>
      <button
        type="button"
        className={`employeesByDeptSelectTrigger ${value ? "hasValue" : ""}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="employeesByDeptSelectTriggerText">
          {value ? label : placeholder}
        </span>
        <span className="material-icons employeesByDeptSelectTriggerIcon">expand_more</span>
      </button>
      {open && (
        <div className="employeesByDeptDropdown">
          <div className="employeesByDeptSearchWrap">
            <input
              type="text"
              className="employeesByDeptSearchInput"
              placeholder={searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="employeesByDeptOptionList" role="listbox">
            {filtered.map(opt => {
              const isDisabled = excludedSet.has(opt.id) && opt.id !== value;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`employeesByDeptOptionItem ${isDisabled ? "isDisabled" : ""}`}
                  role="option"
                  aria-disabled={isDisabled}
                  onClick={() => selectOne(opt.id)}
                  disabled={isDisabled}
                >
                  {opt.label}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="employeesByDeptOptionEmpty">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const getRangeError = row => {
  const start = row.start;
  const end = row.end;
  // No values set -> no error
  if (start == null && end == null) return null;
  // Percentages must be between 0 and 100 (this takes precedence)
  if ((start != null && (start < 0 || start > 100)) || (end != null && (end < 0 || end > 100))) {
    return "percent";
  }
  // When both set, Max must be >= Min
  if (start != null && end != null && end < start) return "order";
  return null;
};

const hasRangeError = row => !!getRangeError(row);

const EMPTY_ROW = { function: [], start: null, end: null, timeFrame: null };

const GrowthByDepartmentInputs = observer(() => {
  const list = dataSearchState?.filterComponentData?.growthByDepartment ?? [];

  const setList = next => {
    dataSearchState.setFilterComponentData({
      ...dataSearchState.filterComponentData,
      growthByDepartment: next
    });
  };

  const updateRow = (index, row) => {
    const next = [...list];
    next[index] = row;
    setList(next);
  };

  const syncRangeErrors = () => {
    const errors = {};
    list.forEach((row, i) => {
      errors[i] = hasRangeError(row);
    });
    dataSearchState.setGrowthByDepartmentErrors(errors);
  };

  React.useEffect(() => {
    syncRangeErrors();
  }, [list]);

  React.useEffect(() => {
    if (list.length === 0) {
      setList([{ ...EMPTY_ROW }]);
    }
  }, []);

  const addRow = () => {
    setList([...list, { ...EMPTY_ROW }]);
  };

  const removeRow = index => {
    const next = list.filter((_, i) => i !== index);
    setList(next);
  };

  const setDepartment = (index, id) =>
    updateRow(index, { ...list[index], function: id ? [id] : [] });
  const setStart = (index, v) => {
    const num = v === "" || v == null ? null : (Number(v) >= 0 ? Number(v) : null);
    updateRow(index, { ...list[index], start: num });
  };
  const setEnd = (index, v) => {
    const num = v === "" || v == null ? null : (Number(v) >= 0 ? Number(v) : null);
    updateRow(index, { ...list[index], end: num });
  };
  const setTimeFrame = (index, v) => {
    updateRow(index, { ...list[index], timeFrame: v || null });
  };

  const timeFrameOptions = [
    { label: "3", value: "THREE" },
    { label: "6", value: "SIX" },
    { label: "12", value: "TWELVE" },
    { label: "24", value: "TWENTY_FOUR" }
  ];

  return (
    <div className="employeesByDeptSection">
      {list.map((row, index) => (
        <div key={index} className="employeesByDeptCard">
          {/*
            Compute error type once per row so both inputs and error message stay in sync.
            "percent" -> values out of 0–100
            "order"   -> max < min
          */}
          {(() => {
            row.__errorType = getRangeError(row);
            return null;
          })()}
          <div className="employeesByDeptTopRow">
            <div className="employeesByDeptSelectContainer">
              <DepartmentSelect
                value={(row.function && row.function[0]) || null}
                onChange={id => setDepartment(index, id)}
                excludedIds={list.flatMap((r, j) => (j !== index ? (r.function || []) : []))}
                placeholder="Department"
                searchPlaceholder="Search"
              />
            </div>
            {list.length > 1 && (
              <button
                type="button"
                className="employeesByDeptRemoveBtn"
                onClick={() => removeRow(index)}
                aria-label="Remove department"
              >
                <span className="material-icons">delete_outline</span>
              </button>
            )}
          </div>
          <div className="employeesByDeptRangeRow">
            <input
              type="number"
              min={0}
              max={100}
              placeholder="Min %"
              className={`employeesByDeptRangeInput ${row.__errorType ? "hasError" : ""}`}
              value={row.start ?? ""}
              onChange={e => setStart(index, e.target.value)}
              aria-label="Min growth percent"
              aria-invalid={!!row.__errorType}
            />
            <span className="employeesByDeptRangeDash">-</span>
            <input
              type="number"
              min={0}
              max={100}
              placeholder="Max %"
              className={`employeesByDeptRangeInput ${row.__errorType ? "hasError" : ""}`}
              value={row.end ?? ""}
              onChange={e => setEnd(index, e.target.value)}
              aria-label="Max growth percent"
              aria-invalid={!!row.__errorType}
            />
          </div>
          <div className="growthByDeptTimeframeRow">
            <span className="growthByDeptTimeframeLabel">in the last</span>
            <select
              className="growthByDeptTimeframeSelect"
              value={row.timeFrame || ""}
              onChange={e => setTimeFrame(index, e.target.value)}
            >
              <option value="">Select</option>
              {timeFrameOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="growthByDeptTimeframeSuffix">Months</span>
          </div>
          {row.__errorType && (
            <div className="employeesByDeptRangeError" role="alert">
              {row.__errorType === "percent"
                ? "Percent values must be between 0 and 100."
                : "Max % must be greater than or equal to Min %."}
            </div>
          )}
        </div>
      ))}
      <button type="button" className="employeesByDeptAddBtn" onClick={addRow}>
        + Add Department
      </button>
    </div>
  );
});

export default GrowthByDepartmentInputs;

